/**
 * Accessible Time Picker Dialog
 *
 * NOTE: This component uses @material-ui/pickers v3 (Material-UI v4) instead of @mui/x-date-pickers (MUI v5).
 * MUI v5 requires React 17+ but this project uses React 16.
 *
 * @mui/x-date-pickers has built-in accessibility features (arrow key increment, proper screen reader support)
 * that would eliminate the custom keyboard handlers below. Consider migrating when React is upgraded.
 *
 * Custom accessibility features implemented here:
 * - Arrow key increment/decrement for hours and minutes
 * - aria-live announcements for value changes
 * - aria-hidden toggling to suppress selection announcements
 *
 * ACCESSIBILITY TRADE-OFF DOCUMENTATION:
 *
 * This component uses aria-hidden="true" on the input during arrow key presses to suppress
 * browser-generated "XX selected, YY unselected" announcements that occur when text is selected.
 *
 * KNOWN ISSUE: This technically violates WCAG SC 4.1.2 (Name, Role, Value) because:
 * - aria-hidden="true" on a focused element removes it from the accessibility tree
 * - The focused element becomes a "ghost focus" - invisible to assistive technology
 * - If a user asks their screen reader to re-read the focused element mid-keypress, they get silence
 *
 * MITIGATING FACTORS:
 * - The aria-hidden state is brief (~50-200ms, between keydown and keyup)
 * - The aria-live region still announces value changes ("Hours: 10", "Minutes: 30")
 * - Users receive immediate feedback about what changed via the live region
 * - The likelihood of Tab navigation during the brief hidden state is very low
 *
 * RISK: Automated accessibility audit tools (axe, WAVE) will flag this as a failure.
 *
 * FUTURE FIX: Upgrade to React 17+ and @mui/x-date-pickers which handles this natively.
 */
import React, { useState, useEffect, useRef } from 'react'
import Button from '@material-ui/core/Button'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import { TextField, Typography, makeStyles } from '@material-ui/core'
import EditableDialogWrapper from './editableDialogWrapper'
import { KeyboardTimePicker } from '@material-ui/pickers'
import { dateTimeConstants } from '../../constants/datetime.constants'
import { format as formatDateFns, isValid, parse } from 'date-fns'
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date'
import { autoFormatTime, parseTimeFormatShape } from '../../helpers/dateFns'

// Visually hidden but accessible to screen readers
const useStyles = makeStyles({
    visuallyHidden: {
        position: 'absolute',
        width: 1,
        height: 1,
        padding: 0,
        margin: -1,
        overflow: 'hidden',
        clip: 'rect(0, 0, 0, 0)',
        whiteSpace: 'nowrap',
        border: 0,
    },
    // Override Material-UI's default error color (#F44336, 3.68:1 contrast)
    // with #AA1308 (5.92:1 contrast) to meet WCAG 2.2 AA 1.4.3 (4.5:1 required)
    errorText: {
        '&.Mui-error': {
            color: '#AA1308',
        },
    },
})

interface TimePickerDialogAccessibleProps {
    title: string
    description?: string
    open: boolean
    onConfirm: (time: Date) => void
    onReset: (time: Date) => void
    onClose: () => void
    currentTime: string
    onChange: (time: Date | null) => void
    resetButtonVisible?: boolean
    confirmButtonText?: string
    timeFormat?: string
    // Gates the ticket #77825 fixes
    resetAndFormatFixEnabled?: boolean
}

const TimePickerDialogAccessible = ({
    title,
    description,
    open,
    onConfirm,
    onReset,
    onClose,
    currentTime,
    onChange,
    resetButtonVisible = true,
    confirmButtonText,
    timeFormat = dateTimeConstants.DEFAULT_TIME_FORMAT,
    resetAndFormatFixEnabled = false,
}: TimePickerDialogAccessibleProps) => {
    const classes = useStyles()
    const parsedTime = parse(currentTime, timeFormat, new Date())

    const formatForInput = (date: Date | null): string => {
        if (!date) return ''
        try {
            return formatDateFns(date, timeFormat)
        } catch {
            return ''
        }
    }

    const [time, setTime] = useState<Date | null>(
        isValid(parsedTime) ? parsedTime : null
    )
    const [inputText, setInputText] = useState<string>(() =>
        resetAndFormatFixEnabled && isValid(parsedTime)
            ? formatForInput(parsedTime)
            : ''
    )
    const [hasError, setHasError] = useState(false)
    const [showError, setShowError] = useState(false)
    const [originalTime, setOriginalTime] = useState<Date | null>(time)
    const [announcement, setAnnouncement] = useState('')
    const [inputAriaHidden, setInputAriaHidden] = useState(false)
    const [resetKey, setResetKey] = useState(0)
    const hasErrorRef = useRef(false)
    const inputRef = useRef<HTMLInputElement>(null)
    const cursorPositionRef = useRef<number | null>(null)
    const rafIdRef = useRef<number | null>(null)
    const helperTextId = `time-picker-helper-${title
        .replace(/\s+/g, '-')
        .toLowerCase()}`
    const confirmDisabledReasonId = `confirm-disabled-reason-${title
        .replace(/\s+/g, '-')
        .toLowerCase()}`

    // Reset state when dialog opens
    useEffect(() => {
        if (open) {
            hasErrorRef.current = false
            setHasError(false)
            setShowError(false)
            const newParsedTime = parse(currentTime, timeFormat, new Date())
            const initialTime = isValid(newParsedTime) ? newParsedTime : null
            setTime(initialTime)
            setOriginalTime(initialTime)
            if (resetAndFormatFixEnabled) {
                setInputText(formatForInput(initialTime))
            }
        }
    }, [open, currentTime, timeFormat])

    // Cleanup RAF on unmount to prevent memory leaks
    useEffect(() => {
        return () => {
            if (rafIdRef.current !== null) {
                cancelAnimationFrame(rafIdRef.current)
            }
        }
    }, [])

    // Restore cursor position and selection after time state updates via arrow keys
    useEffect(() => {
        if (cursorPositionRef.current !== null && inputRef.current) {
            const cursorPosition = cursorPositionRef.current
            // Derive the separator position from the rendered value (not the
            // format), so it's correct whether the hour is 1 or 2 digits.
            const value = inputRef.current.value
            const separatorIndex =
                value.indexOf(':') >= 0
                    ? value.indexOf(':')
                    : timeFormat.indexOf(':')
            const minutesEndIndex = separatorIndex + 3

            // Determine selection range based on segment
            let selectionStart: number
            let selectionEnd: number
            if (cursorPosition === 0) {
                // Hour segment
                selectionStart = 0
                selectionEnd = separatorIndex
            } else if (cursorPosition >= minutesEndIndex) {
                // AM/PM segment
                selectionStart = minutesEndIndex
                selectionEnd = inputRef.current.value.length
            } else {
                // Minute segment
                selectionStart = separatorIndex + 1
                selectionEnd = minutesEndIndex
            }

            // requestAnimationFrame defers focus/selection until after React's render cycle and browser paint.
            // More reliable than setTimeout(0) across browsers including Safari.
            rafIdRef.current = requestAnimationFrame(() => {
                if (inputRef.current) {
                    inputRef.current.focus()
                    inputRef.current.setSelectionRange(
                        selectionStart,
                        selectionEnd
                    )
                }
                rafIdRef.current = null
            })
            cursorPositionRef.current = null
        }
    }, [time, timeFormat])

    const is24HourFormat = (format: string): boolean => {
        const uses24HourToken = /H/.test(format) && !/h/.test(format)
        const hasAmPmMarker = /a+/.test(format)
        return uses24HourToken && !hasAmPmMarker
    }

    const is24Hours = is24HourFormat(timeFormat)

    const getTimeExample = (): string => {
        const fallback = is24Hours ? '09:30' : '09:30 AM'

        if (!resetAndFormatFixEnabled) {
            return fallback
        }
        try {
            return formatDateFns(new Date(2000, 0, 1, 9, 30, 0), timeFormat)
        } catch {
            return fallback
        }
    }

    const getFormatHint = (): string => {
        return `Format: ${timeFormat}, e.g. ${getTimeExample()}`
    }

    const getInvalidTimeMessage = (): string => {
        return `Enter a time in ${timeFormat} format, for example ${getTimeExample()}`
    }

    const clearErrorState = () => {
        hasErrorRef.current = false
        setHasError(false)
        setShowError(false)
    }

    const handleReset = () => {
        clearErrorState()
        const restored = originalTime ? new Date(originalTime) : null
        setTime(restored)
        // Force picker to remount to clear invalid text from input
        setResetKey(prev => prev + 1)

        if (resetAndFormatFixEnabled) {
            setInputText(formatForInput(restored))
            onReset(restored ?? new Date())
        }
    }

    const timeFormatShape = resetAndFormatFixEnabled
        ? parseTimeFormatShape(timeFormat)
        : null

    const handleFreeTextChange = (
        e: React.ChangeEvent<HTMLInputElement>
    ): void => {
        if (!timeFormatShape) return
        const typed = e.target.value
        // Auto-format toward the target format as the user types (insert
        // separators, accept a 1- or 2-digit hour). Detect a genuine deletion
        // from the event's inputType so backspace can remove auto-inserted
        // separators
        const inputType = (e.nativeEvent as InputEvent).inputType
        const isDeleting = inputType
            ? inputType.startsWith('delete')
            : typed.length < inputText.length
        const next = isDeleting ? typed : autoFormatTime(typed, timeFormatShape)
        setInputText(next)

        const parsed = parse(next, timeFormat, new Date())
        const isValidTime = next.trim() !== '' && isValid(parsed)
        // Only a non-empty, unparseable value is an "error"; empty just disables
        // Confirm (via !time) without showing a validation message.
        hasErrorRef.current = next.trim() !== '' && !isValidTime
        setHasError(hasErrorRef.current)
        setTime(isValidTime ? parsed : null)
        if (isValidTime) {
            setShowError(false)
        }
        onChange(isValidTime ? parsed : null)
    }

    const handleFreeTextBlur = (): void => {
        setShowError(hasErrorRef.current)
    }

    const parseTimeFromInput = (inputValue: string): Date => {
        const parts = inputValue.split(':')
        const hours = parseInt(parts[0], 10) || 0
        const minutes = parseInt(parts[1], 10) || 0
        const newTime = new Date()
        newTime.setHours(hours, minutes, 0, 0)
        return newTime
    }

    const announceTimeChange = (
        newTime: Date,
        segment: 'hour' | 'minute' | 'ampm'
    ) => {
        const hours = newTime.getHours()
        const minutes = newTime.getMinutes()
        const formattedHours = is24Hours
            ? hours.toString().padStart(2, '0')
            : (hours % 12 || 12).toString()
        const formattedMinutes = minutes.toString().padStart(2, '0')
        const amPm = hours >= 12 ? 'PM' : 'AM'

        switch (segment) {
            case 'hour':
                setAnnouncement(`Hours: ${formattedHours}`)
                break
            case 'minute':
                setAnnouncement(`Minutes: ${formattedMinutes}`)
                break
            case 'ampm':
                setAnnouncement(amPm)
                break
        }
    }

    const handleArrowKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') {
            return
        }

        e.preventDefault()

        const input = e.target as HTMLInputElement
        const cursorPosition = input.selectionStart ?? 0
        // Use the rendered value's separator (not the format), so the segment
        // boundaries are right whether the hour is 1 or 2 digits.
        const separatorIndex =
            input.value.indexOf(':') >= 0
                ? input.value.indexOf(':')
                : timeFormat.indexOf(':')

        // Determine which segment the cursor is in
        // For 12-hour format "hh:mm a": hours (0-2), minutes (3-4), AM/PM (6+)
        const minutesEndIndex = separatorIndex + 3 // After "mm"
        const isHourSegment = cursorPosition <= separatorIndex
        const isAmPmSegment = !is24Hours && cursorPosition >= minutesEndIndex

        // Store which segment to select after update
        // ACCESSIBILITY TRADE-OFF: Temporarily hide input from screen reader to suppress
        // browser "XX selected, YY unselected" announcements. See file header for details.
        // The aria-live region still announces the value change during this brief period.
        setInputAriaHidden(true)
        if (isHourSegment) {
            cursorPositionRef.current = 0
        } else if (isAmPmSegment) {
            cursorPositionRef.current = minutesEndIndex
        } else {
            cursorPositionRef.current = separatorIndex + 1
        }

        const increment = e.key === 'ArrowUp' ? 1 : -1
        const newTime = time ? new Date(time) : parseTimeFromInput(input.value)

        if (isAmPmSegment) {
            // Toggle AM/PM by adding or subtracting 12 hours
            newTime.setHours(newTime.getHours() + 12)
            clearErrorState()
            announceTimeChange(newTime, 'ampm')
        } else if (isHourSegment) {
            newTime.setHours(newTime.getHours() + increment)
            clearErrorState()
            announceTimeChange(newTime, 'hour')
        } else {
            newTime.setMinutes(newTime.getMinutes() + increment)
            clearErrorState()
            announceTimeChange(newTime, 'minute')
        }

        setTime(newTime)
        if (resetAndFormatFixEnabled) {
            setInputText(formatForInput(newTime))
        }
        onChange(newTime)
    }

    const handleArrowKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            // Restore screen reader access after selection is complete
            setInputAriaHidden(false)
        }
    }

    return (
        <EditableDialogWrapper title={title} open={open} onClose={onClose}>
            <DialogContent dividers>
                {description && (
                    <Typography gutterBottom>{description}</Typography>
                )}
                {resetAndFormatFixEnabled ? (
                    <TextField
                        fullWidth
                        inputRef={inputRef}
                        // Show a concrete example (e.g. "9:30 am") rather than
                        // the raw format tokens, so it's obvious what to type.
                        placeholder={getTimeExample()}
                        value={inputText}
                        error={showError}
                        helperText={
                            showError
                                ? getInvalidTimeMessage()
                                : getFormatHint()
                        }
                        onChange={handleFreeTextChange}
                        onBlur={handleFreeTextBlur}
                        FormHelperTextProps={{
                            id: helperTextId,
                            className: classes.errorText,
                        }}
                        inputProps={{
                            'aria-label': title,
                            'aria-describedby': helperTextId,
                            'aria-invalid': showError ? 'true' : undefined,
                            'aria-hidden': inputAriaHidden ? 'true' : undefined,
                            onKeyDown: handleArrowKeyDown,
                            onKeyUp: handleArrowKeyUp,
                        }}
                    />
                ) : (
                    <KeyboardTimePicker
                        key={resetKey}
                        helperText={
                            showError
                                ? getInvalidTimeMessage()
                                : getFormatHint()
                        }
                        FormHelperTextProps={{
                            error: showError,
                            id: helperTextId,
                            className: classes.errorText,
                        }}
                        placeholder={timeFormat} // e.g., "HH:mm" or "hh:mm a"
                        error={showError}
                        ampm={!is24Hours}
                        format={timeFormat}
                        value={time}
                        KeyboardButtonProps={{
                            'aria-label': 'Open time picker',
                        }}
                        onChange={(selectedTime: MaterialUiPickersDate) => {
                            // Track validity - null or invalid Date means user entered invalid input
                            const isValidTime =
                                selectedTime && isValid(selectedTime)
                            hasErrorRef.current = !isValidTime
                            setHasError(!isValidTime)

                            // Clear error immediately when user enters valid value
                            if (isValidTime) {
                                setShowError(false)
                                setTime(selectedTime)
                            }
                            onChange(selectedTime)
                        }}
                        // KeyboardTimePicker doesn't natively support arrow key increment/decrement with v3 @material-ui/pickers (deprecated).
                        // This custom handler enables users to adjust hours or minutes using ↑/↓ arrow keys based on cursor position.
                        // Note: In newer versions of @mui/x-date-pickers, this functionality is built-in and may not require a custom handler (https://mui.com/material-ui/migration/pickers-migration/).
                        inputProps={{
                            'aria-label': title,
                            'aria-describedby': helperTextId,
                            'aria-invalid': showError ? 'true' : undefined,
                            'aria-hidden': inputAriaHidden ? 'true' : undefined,
                        }}
                        InputProps={{
                            inputRef: inputRef,
                            onBlur: () => setShowError(hasErrorRef.current),
                            onKeyUp: handleArrowKeyUp,
                            onKeyDown: handleArrowKeyDown,
                        }}
                        InputAdornmentProps={{ style: { display: 'none' } }}
                    />
                )}
                {/* Visually hidden live region for screen reader announcements */}
                <span
                    role='status'
                    aria-live='polite'
                    aria-atomic='true'
                    className={classes.visuallyHidden}
                >
                    {announcement}
                </span>
            </DialogContent>
            <DialogActions>
                {resetButtonVisible && (
                    <Button onClick={handleReset} color='secondary'>
                        Reset
                    </Button>
                )}
                {/* Visually hidden text explaining why confirm is disabled */}
                {(hasError || !time) && (
                    <span
                        id={confirmDisabledReasonId}
                        className={classes.visuallyHidden}
                    >
                        {!time
                            ? 'Enter a valid time to confirm'
                            : 'Fix the time format error to confirm'}
                    </span>
                )}
                <Button
                    variant='contained'
                    onClick={() => time && onConfirm(time)}
                    color='secondary'
                    disabled={hasError || !time}
                    aria-describedby={
                        hasError || !time ? confirmDisabledReasonId : undefined
                    }
                >
                    {confirmButtonText || 'Confirm'}
                </Button>
            </DialogActions>
        </EditableDialogWrapper>
    )
}

export default TimePickerDialogAccessible
